// Phase 2.10 — REST-backed referralService.
//
// Mirrors the legacy public surface: pickUniqueCode / findTenantByCode /
// assignCodeToTenant / recordPending / listByReferrer / getStats /
// ensureTenantCode. Pure helpers (generateReferralCode,
// normaliseReferralCode, buildReferralLink, copyToClipboard) stay in the
// legacy module.

import { getAccessToken } from '../contexts/AuthContextV2';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4001';

async function apiFetch(path, options = {}) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  let body = null;
  try { body = await res.json(); } catch { /* non-json */ }
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const normalise = (raw) => (typeof raw === 'string' ? raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase() : '');

export const referralService = {
  // Server picks a unique code; call ensureTenantCode instead of generating
  // client-side.
  async pickUniqueCode() {
    const { referralCode } = await apiFetch('/api/referrals/codes/ensure', { method: 'POST' });
    return referralCode;
  },

  async findTenantByCode(code) {
    const c = normalise(code);
    if (!c) return null;
    try {
      const { tenant } = await apiFetch(`/api/referrals/codes/${encodeURIComponent(c)}`);
      return { id: tenant.id, name: tenant.name, referralCode: tenant.referralCode };
    } catch (err) {
      if (err.status === 404) return null;
      console.warn('referralServiceV2.findTenantByCode failed:', err.message);
      return null;
    }
  },

  // assignCodeToTenant collapses into ensureTenantCode server-side; the
  // legacy explicit-assign path is unused by Phase-1 callers.
  async assignCodeToTenant(_tenantId, _code) {
    return referralService.pickUniqueCode();
  },

  async recordPending({ referrerTenantId, referredTenantId, referralCode }) {
    if (!referrerTenantId) return null;
    if (referrerTenantId === referredTenantId) return null;
    try {
      const { referral, alreadyExisted } = await apiFetch('/api/referrals', {
        method: 'POST',
        body: JSON.stringify({
          referrerTenantId,
          referralCode: referralCode ? normalise(referralCode) : null
        })
      });
      return { ...referral, alreadyExisted: !!alreadyExisted };
    } catch (err) {
      console.warn('referralServiceV2.recordPending failed:', err.message);
      return null;
    }
  },

  async listByReferrer(referrerTenantId) {
    if (!referrerTenantId) return [];
    try {
      const { referrals } = await apiFetch('/api/referrals');
      return referrals.filter((r) => r.referrerTenantId === referrerTenantId);
    } catch (err) {
      console.warn('referralServiceV2.listByReferrer failed:', err.message);
      return [];
    }
  },

  async getStats(referrerTenantId) {
    const rows = await referralService.listByReferrer(referrerTenantId);
    let pending = 0, paying = 0, cancelled = 0;
    for (const r of rows) {
      if (r.status === 'paying') paying += 1;
      else if (r.status === 'cancelled') cancelled += 1;
      else pending += 1;
    }
    return { total: rows.length, pending, paying, cancelled };
  },

  async ensureTenantCode(_tenantId) {
    // tenantId comes from JWT.
    const { referralCode } = await apiFetch('/api/referrals/codes/ensure', { method: 'POST' });
    return referralCode;
  }
};

export default referralService;
